import NudgeDto, {
  NudgeDeliveryChannel,
  UsageNudgeAudience,
} from "../../types/nudges/NudgeDto";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

/** Matches UsageJourneyStatusConstants.NudgeCooldownHours on the API. */
export const USAGE_NUDGE_COOLDOWN_HOURS = 72;

export function isUsageNudgeOnCooldown(
  lastSentAt: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!lastSentAt) return false;

  const cooldownEndsAt =
    new Date(lastSentAt).getTime() + USAGE_NUDGE_COOLDOWN_HOURS * 60 * 60 * 1000;
  return cooldownEndsAt > nowMs;
}

export function formatUsageNudgeCooldown(isoDate: string, locale?: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(isoDate));
  } catch {
    return isoDate;
  }
}

export function formatUsageNudgeAge(isoDate: string): string {
  return dayjs(isoDate).fromNow();
}

export function formatUsageNudgeAbsolute(
  isoDate: string,
  locale?: string,
): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "long",
      timeStyle: "short",
    }).format(new Date(isoDate));
  } catch {
    return isoDate;
  }
}

export function formatUsageNudgeRetryDay(
  isoDate: string,
  locale?: string,
): string {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: "long" }).format(
      new Date(isoDate),
    );
  } catch {
    return isoDate;
  }
}

export function getUsageNudgeDeliveryChannelLabel(
  channel: NudgeDeliveryChannel,
  t: (key: string, fallback: string) => string,
): string {
  switch (channel) {
    case "EMAIL":
      return t(
        "Analytics.Widgets.Usage.Nudge.DeliveryChannel.Email",
        "Email",
      );
    case "NATIVE":
      return t(
        "Analytics.Widgets.Usage.Nudge.DeliveryChannel.Native",
        "Mobile app notification",
      );
    case "YAPSTER":
      return t(
        "Analytics.Widgets.Usage.Nudge.DeliveryChannel.Yapster",
        "Yapster",
      );
    case "WORKPLACE":
      return t(
        "Analytics.Widgets.Usage.Nudge.DeliveryChannel.Workplace",
        "Workplace",
      );
    case "WORKVIVO":
      return t(
        "Analytics.Widgets.Usage.Nudge.DeliveryChannel.WorkVivo",
        "Workvivo",
      );
    default:
      return channel;
  }
}

export function usageNudgeAudienceForType(
  nudgeType: NudgeDto["nudgeType"],
): UsageNudgeAudience {
  return nudgeType === "USAGE_JOURNEY_COMPLETION_EMPLOYEE" ? "EMPLOYEE" : "MANAGER";
}

const NUDGEABLE_LEGEND_KEYS = new Set([
  "NOT-STARTED",
  "PARTIALLY-COMPLETE",
  "WAITING-FOR-EMPLOYEE",
  "WAITING-FOR-MANAGER",
]);

export function isUsageNudgeableLegendStatus(status: string | null): boolean {
  if (!status) return false;
  return NUDGEABLE_LEGEND_KEYS.has(status.toUpperCase());
}
